import { Decimal, useCurrenciesQuery } from '@capsicum/common';
import { useNCWBalance, usePartners } from '@capsicum/socios-core';
import { useMemo } from 'react';

import { type MigrateableFanToken } from '../models';

export const useMigrateableFanTokens = () => {
	const { getPartnerBy } = usePartners();
	const { isPending: isNCWBalanceLoading, ncwBalance } = useNCWBalance();
	const { data: allCurrencies, isLoading: isCurrenciesLoading } = useCurrenciesQuery({ bustCache: true });

	const currencies = ncwBalance?.currencies;

	return {
		isLoading: isNCWBalanceLoading || isCurrenciesLoading,
		migrateableTokens: useMemo(() => {
			if (!currencies || !allCurrencies) return [];

			return currencies.reduce<Array<MigrateableFanToken>>((tokens, currency) => {
				const usable = new Decimal(currency.usableAmount);
				const staked = new Decimal(currency.stakedAmount).minus(currency.totalLocked ?? 0);
				const unstaked = new Decimal(currency.totalAmount)
					.minus(currency.usableAmount)
					.minus(currency.stakedAmount);

				const amount = usable.plus(staked).plus(unstaked);

				if (
					!currency.currencySmartContract ||
					amount.equals(0) ||
					currency.currency.type !== 'nonDecimalVoteToken'
				) {
					return tokens;
				}

				const partner = getPartnerBy(({ tokenSymbol }) => currency.currency.symbol === tokenSymbol);

				if (!partner || !partner.id) return tokens;

				const v2Currency = allCurrencies.find(
					currency => currency.id === partner.currencyId && currency.decimalDetail === 18
				);

				if (!v2Currency || !v2Currency.smartContractAddress) return tokens;

				tokens.push({
					amount: amount.toString(),
					logo: !partner.images?.partnerLogo
						? undefined
						: {
								blurHash: partner.images.partnerLogo.blurHash,
								url: partner.images.partnerLogo.url
							},
					name: partner.name,
					partnerID: partner.id,
					shortCode: currency.currency.shortCode,
					smartContractAddress: currency.currencySmartContract,
					staked: staked.toString(),
					unstaked: unstaked.toString(),
					usable: usable.toString(),
					v2SmartContractAddress: v2Currency.smartContractAddress
				});

				return tokens;
			}, []);
		}, [currencies, getPartnerBy, allCurrencies])
	};
};
